import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, PlayCircle } from "lucide-react";
import {
  SERVICES,
  WHY_US,
  DESTINATIONS,
  TESTIMONIALS,
  STATS,
  QUOTE_WA,
  buildWhatsAppLink,
} from "../lib/constants";
import { useTranslation } from "../lib/i18n";
import SectionHeading from "../components/SectionHeading";
import ServiceIcon from "../components/ServiceIcon";
import HeroVideoMontage from "../components/HeroVideoMontage";

const HERO_POSTER =
  "https://images.pexels.com/photos/12820604/pexels-photo-12820604.jpeg";

// Cinematic corporate travel montage — 7 scenes, ~3.6s each (~25s total)
// Scene order mirrors the corporate travel journey: terminal → lounge →
// boarding → cabin → runway → private apron → global skyline.
const HERO_SCENES = [
  { src: "https://cdn.coverr.co/videos/coverr-walking-with-suitcase-4717/1080p.mp4" },
  { src: "https://cdn.coverr.co/videos/coverr-typing-on-a-laptop-at-the-airport-2673/1080p.mp4" },
  { src: "https://cdn.coverr.co/videos/coverr-boarding-a-plane-5868/1080p.mp4" },
  { src: "https://cdn.coverr.co/videos/coverr-woman-looking-out-a-plane-window-3383/1080p.mp4" },
  { src: "https://cdn.coverr.co/videos/coverr-planes-heading-to-the-runway-8804/1080p.mp4" },
  { src: "https://cdn.coverr.co/videos/coverr-private-jets-at-an-airport-2971/1080p.mp4" },
  { src: "https://videos.pexels.com/video-files/2053855/2053855-hd_1920_1080_30fps.mp4" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  const { t } = useTranslation();
  // Map STATS labels to translation keys
  const statKey = {
    "Years of expertise": "hero.stat.years",
    "Countries served": "hero.stat.countries",
    "Client retention": "hero.stat.retention",
    "Traveller support": "hero.stat.support",
  };
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroVideoMontage
            scenes={HERO_SCENES}
            sceneDuration={3.6}
            fadeDuration={650}
            poster={HERO_POSTER}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/85 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/40 via-transparent to-[#05070C]" />
          <div className="absolute inset-0 bg-radial-gold opacity-45" />
        </div>

        <div className="relative container-x pb-24 sm:pb-32 lg:pb-40 pt-32 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.div variants={fadeUp} className="eyebrow mb-7">
              <span className="hairline" />
              {t("hero.eyebrow")}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-white"
            >
              {t("hero.title1")}{" "}
              <span className="gold-text italic">{t("hero.titleAccent")}</span> {t("hero.title2")}{" "}
              <span className="text-white/80">{t("hero.title3")}</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-8 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={3}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={QUOTE_WA}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-quote-cta"
                className="btn-gold group"
              >
                {t("hero.ctaQuote")}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="btn-ghost-gold"
              >
                {t("hero.ctaServices")}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={4}
              className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl"
            >
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="border-l border-gold/30 pl-4"
                  data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="font-display text-3xl sm:text-4xl text-white">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.28em] text-white/55">
                    {t(statKey[s.label] || s.label)}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-6 right-6 hidden lg:flex items-center gap-3 text-white/45 text-[10px] uppercase tracking-[0.4em]">
          <span>{t("hero.scroll")}</span>
          <span className="h-px w-10 bg-white/30" />
        </div>
      </section>

      {/* MARQUEE / TRUST BAR */}
      <section className="relative border-y border-white/5 py-8 bg-[#0B1120]/40">
        <div className="container-x flex flex-wrap items-center justify-between gap-x-12 gap-y-4 text-white/40 text-xs uppercase tracking-[0.32em]">
          <span>IATA Accredited</span>
          <span className="hidden sm:inline">·</span>
          <span>800+ Airlines</span>
          <span className="hidden sm:inline">·</span>
          <span>1M+ Properties</span>
          <span className="hidden sm:inline">·</span>
          <span>180 Countries</span>
          <span className="hidden sm:inline">·</span>
          <span>24/7 Support</span>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section" id="services">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Our Services"
                title="A complete travel desk, engineered for executives."
                goldWord="executives"
              />
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/60 leading-relaxed">
                From a single domestic flight to a multi-leg global roadshow —
                every itinerary is hand-built, policy-aligned and quietly
                optimised for cost, comfort and time.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`service-card-${s.id}`}
              >
                <Link
                  to={`/services/${s.id}`}
                  data-testid={`service-card-link-${s.id}`}
                  className="group relative bg-[#05070C] p-8 lg:p-10 hover:bg-[#0B1120] transition-colors duration-500 block h-full"
                >
                  <div className="flex items-center justify-between mb-10">
                    <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/30 text-gold group-hover:border-gold group-hover:shadow-[0_0_24px_rgba(212,175,55,0.25)] transition-all duration-500">
                      <ServiceIcon name={s.icon} className="h-5 w-5" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-xl text-white mb-2">{s.title}</h3>
                  <p className="text-xs uppercase tracking-[0.22em] text-gold/70 mb-5">
                    {s.short}
                  </p>
                  <p className="text-sm text-white/55 leading-relaxed">
                    {s.description}
                  </p>
                  <div className="mt-8 flex items-center gap-2 text-gold text-xs uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-500">
                    Enquire
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — split */}
      <section className="section bg-[#0B1120]/40 border-y border-white/5">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/30576168/pexels-photo-30576168.jpeg"
                alt="Executive boarding a private aircraft"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070C] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-8 -right-8 hidden md:block glass p-6 max-w-[260px]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">
                Live now
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                "Reroute confirmed. Driver on standby at JNB. Lounge access added."
              </p>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/45">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                Account team · 02:47 AM
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Why Choose Us"
              title="Discreet. Decisive. Always on."
              goldWord="Decisive."
              subtitle="Six promises we keep across every itinerary, every traveller, every time zone."
            />
            <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-8">
              {WHY_US.map((w, i) => (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`why-card-${i}`}
                  className="flex gap-5"
                >
                  <span className="grid place-items-center shrink-0 h-11 w-11 rounded-full border border-gold/30 text-gold">
                    <ServiceIcon name={w.icon} className="h-4 w-4" />
                  </span>
                  <div>
                    <h4 className="font-display text-lg text-white mb-1.5">
                      {w.title}
                    </h4>
                    <p className="text-sm text-white/55 leading-relaxed">
                      {w.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="section">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <SectionHeading
              eyebrow="Featured Destinations"
              title="Cities the world conducts business in."
              goldWord="business"
            />
            <Link
              to="/destinations"
              data-testid="destinations-view-all"
              className="btn-ghost-gold self-start lg:self-auto"
            >
              View all destinations
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {DESTINATIONS.slice(0, 6).map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                data-testid={`destination-card-${d.id}`}
                className={`group relative overflow-hidden ${
                  i === 0 ? "lg:col-span-2 lg:row-span-2" : i === 3 ? "lg:col-span-2" : ""
                }`}
              >
                <div className={`relative ${i === 0 ? "aspect-square lg:aspect-auto lg:h-full" : "aspect-[4/5]"} overflow-hidden`}>
                  <img
                    src={d.image}
                    alt={d.name}
                    className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070C] via-[#05070C]/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 lg:p-7">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold/90 mb-2">
                      {d.region}
                    </div>
                    <h3 className="font-display text-2xl lg:text-3xl text-white">
                      {d.name}
                    </h3>
                    <p className="mt-2 text-sm text-white/70 max-h-0 overflow-hidden group-hover:max-h-24 transition-[max-height] duration-700">
                      {d.blurb}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE PROMO BAND */}
      <section className="section bg-[#0B1120] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-gold opacity-50" />
        <div className="container-x relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Corporate Solutions"
              title="A travel programme as sophisticated as your operations."
              goldWord="sophisticated"
              subtitle="Policy management, expense optimisation, dedicated account leadership and live analytics — all in one quiet, capable team."
            />
            <Link
              to="/corporate-solutions"
              data-testid="corp-solutions-cta"
              className="btn-gold mt-10"
            >
              Explore corporate suite
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {[
              { label: "Avg. policy compliance", value: "97%" },
              { label: "Spend visibility", value: "Live" },
              { label: "Avg. response time", value: "<8 min" },
              { label: "Bookings via SLA", value: "100%" },
            ].map((m) => (
              <div
                key={m.label}
                className="glass p-6"
                data-testid={`corp-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-display text-3xl text-white">{m.value}</div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/50">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container-x">
          <SectionHeading
            eyebrow="Client Voices"
            title="Trusted by leadership teams across Africa and beyond."
            goldWord="leadership"
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`testimonial-${i}`}
                className="glass p-8 hover:border-gold/40 transition-colors duration-500 relative"
              >
                <div className="text-gold font-display text-5xl leading-none mb-4">"</div>
                <blockquote className="text-white/80 leading-relaxed text-[15px]">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-7 pt-6 border-t border-white/5">
                  <div className="text-white text-sm">{t.author}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.24em] text-white/45">
                    {t.role}
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1677129667171-92abd8740fa3?w=1800&q=80"
            alt="Luxury hotel lobby"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/85 to-[#05070C]/55" />
        </div>
        <div className="relative container-x py-28 lg:py-40">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6">
              <span className="hairline" /> Ready when you are
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              Ready to upgrade your <span className="gold-text">travel experience?</span>
            </h2>
            <p className="mt-6 text-white/65 max-w-xl">
              Tell us where you're going. We'll handle the how, the where, the when — and the unexpected.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={QUOTE_WA}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-quote-button"
                className="btn-gold"
              >
                Get a Quote on WhatsApp
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <a
                href={buildWhatsAppLink("Hi Beyond Borders, I'd like to talk to a travel consultant.")}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-whatsapp-button"
                className="btn-ghost-gold"
              >
                Chat with us
                <PlayCircle className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
